import React, { useCallback, useMemo, useState } from 'react'
import { MultiSelect } from '@musinsa/layer-components-desktop'
import styled from 'styled-components'

export interface SearchableMultiSelectOption {
  value: string
  label: string
}

export interface SearchableMultiSelectProps {
  options: SearchableMultiSelectOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  floatingBlockLabel?: string
  required?: boolean
  disabled?: boolean
  showSelectAll?: boolean
  showSearch?: boolean
  width?: number
  // 서버 검색 연동용 — 검색어 변경을 부모에 알린다 (부모가 options 를 검색 결과로 갱신).
  // 내부 클라이언트 필터는 그대로 동작하므로 서버가 이미 거른 결과에 무해하다. (LOGISPRD-10268)
  onSearchValueChange?: (value: string) => void
  // "미선택 = 전체" 로 저장하는 화면용. 검색어가 없을 때 '전체' 를 누르면 옵션을 전부 나열하지 않고
  // 빈 배열로 둔다 — 트리거에도 "A 외 662" 대신 placeholder('전체')가 보인다.
  // 검색어가 있을 때는 기존대로 검색 결과만 추가/해제한다(전체가 아니라 부분 선택이므로).
  emptyMeansAll?: boolean
  // 트리거 높이. 기본 44px 는 FilterListV2 의 단일 Select 와 맞춘 값이고,
  // 32px 짜리 input/select 를 쓰는 폼(프로모션 등록 모달)에서는 32 를 넘겨 높이를 맞춘다.
  triggerHeight?: number
  // 드롭다운 행 오른쪽에 옵션 코드(value)를 회색으로 같이 보여준다.
  // 물류카테고리처럼 검색이 코드로만 되는 목록에서 왜 걸렸는지 보이게 하려는 것. 트리거에는 붙지 않는다.
  showOptionValue?: boolean
  // 검색어를 부모가 소유(controlled). 상시 마운트 모달처럼 부모가 검색 상태를 리셋해야 할 때 쓴다.
  // 미전달이면 내부 state 로 동작한다. (LOGISPRD-11276)
  searchValue?: string
}

// layer MultiSelect 기반 멀티셀렉트(검색·전체선택). 드롭다운이 popover로 떠 FilterListV2의
// overflow:hidden 행에서도 잘리지 않으며(짤림 해결), trigger 높이를 44px로 고정해 단일 Select와 정렬을 맞춘다.
//
// [전체선택을 "검색 결과"로 한정하는 설계 — LOGISPRD-9505]
// layer의 MultiSelect.OptionAll / Trigger 요약은 모두 Root의 `options` prop(context)을 기준으로 동작한다.
//  - OptionAll: isSelected = options.every(...) / 클릭 시 onValueChange(options.map(...))  (전체를 "교체")
//  - Trigger 요약(summaryWithCount): 선택값 라벨을 options.find(...)로 해석
// 따라서 Root.options에 filteredOptions를 넘기면 (1)검색 중 필터 밖 선택값의 라벨이 깨지고
// (2)OptionAll이 기존 선택을 보존하지 못한다.
// → Root.options에는 항상 "전체 options"를 넘겨 Trigger 라벨 해석이 항상 정확하도록 두고,
//   "전체" 행은 Radix Select.Item(빈 value 금지·내부 토글) 제약을 피하기 위해 라이브러리 Option 컴포넌트가
//   아닌 일반 presentational 행으로 직접 렌더한다. 클릭 시 현재 검색 결과(filteredOptions)만 추가/해제한다.
//   (기존 선택값은 보존)
const Wrapper = styled.div<{ $width?: number; $triggerHeight: number; $placeholderAsValue?: boolean }>`
  ${({ $width }) => ($width ? `min-width: ${$width}px; width: ${$width}px;` : 'width: 100%;')}

  & > button {
    height: ${({ $triggerHeight }) => $triggerHeight}px;
    width: 100%;
  }

  /* 값 영역은 line-height 가 font-size 와 같고(15px) overflow:hidden 이라
     g·y 같은 descender 하단이 잘린다 (LOGISPRD-11294).
     같은 선택자에 floatingBlockLabel div 도 걸리지만, 그쪽은 absolute + 중앙정렬이라 위치가 그대로다 */
  & > button > div,
  & > button > div * {
    line-height: 1.4;
  }

  /* '전체' 는 placeholder 자리에 그려지지만 실제 선택값이므로, 흐린 글씨로 보이지 않게 되돌린다 */
  ${({ $placeholderAsValue }) =>
    $placeholderAsValue &&
    `
    & > button:not(:disabled) {
      background: #fff;
    }
    & > button:not(:disabled),
    & > button:not(:disabled) * {
      color: #1a1a1a;
    }
  `}
`

// "전체" 행 — 라이브러리 Option(Radix Select.Item) 을 쓰지 않고 직접 렌더해 value 제약/토글 충돌을 피한다.
// emptyMeansAll 모드에서는 개별 행도 같은 방식으로 그린다. 라이브러리 Option 의 체크 상태는
// Root.value 에서만 나오는데(SelectOptionProps 가 isSelected 를 Omit), 그 모드의 전체 상태는
// value=[] 라서 개별 행이 전부 빈 칸으로 보이기 때문이다.
const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  user-select: none;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
  &:focus-visible {
    outline: none;
    background: rgba(0, 0, 0, 0.06);
  }
`

// 행 오른쪽 코드 표기 (showOptionValue)
const OptionValue = styled.span`
  margin-left: auto;
  padding-left: 8px;
  color: #999;
  font-size: 12px;
  flex-shrink: 0;
`

const SelectAllCheck = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1.5px solid ${({ $checked }) => ($checked ? '#1a1a1a' : '#d1d1d1')};
  background: ${({ $checked }) => ($checked ? '#1a1a1a' : '#fff')};
  color: #fff;
  font-size: 11px;
  line-height: 1;
`

export function SearchableMultiSelect({
  options,
  value,
  onValueChange,
  placeholder = '전체',
  floatingBlockLabel,
  required,
  disabled = false,
  showSelectAll = true,
  showSearch = true,
  width,
  onSearchValueChange,
  emptyMeansAll = false,
  triggerHeight = 44,
  showOptionValue = false,
  searchValue: controlledSearchValue,
}: SearchableMultiSelectProps) {
  const [internalSearchValue, setInternalSearchValue] = useState('')
  const searchValue = controlledSearchValue ?? internalSearchValue
  const setSearchValue = setInternalSearchValue

  // Radix Select.Item 은 빈 문자열 value 를 금지(런타임 throw)하므로, 빈 value 옵션을 원천 제거한다.
  const safeOptions = useMemo(() => options.filter((opt) => opt.value !== ''), [options])

  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return safeOptions
    const lower = searchValue.toLowerCase()
    return safeOptions.filter((opt) => opt.label.toLowerCase().includes(lower) || opt.value.toLowerCase().includes(lower))
  }, [safeOptions, searchValue])

  const filteredValues = useMemo(() => filteredOptions.map((opt) => opt.value), [filteredOptions])

  // 검색어가 없는 상태에서만 '전체' = 빈 배열 로 다룬다 (emptyMeansAll)
  const isAllAsEmpty = emptyMeansAll && !searchValue.trim()

  // emptyMeansAll 모드의 체크 표시 상태. 값이 빈 배열이어도 '전체 선택'과 '전부 해제' 두 가지로 보일 수 있어
  // (저장값은 둘 다 전체) 화면 상태만 따로 들고 있는다.
  const [allChecked, setAllChecked] = useState(() => value.length === 0)
  // showSelectAll 이 꺼져 있으면 '전체' 를 고를 수단이 없다 — 트리거·행 체크도 전체로 보이면 안 된다.
  // 상위 축이 전체라 하위 축의 전체가 금지된 화면(프로모션 등록 모달의 브랜드·카테고리)이 이 경로다 (LOGISPRD-11341)
  const showAllChecked = showSelectAll && isAllAsEmpty && value.length === 0 && allChecked

  // 현재 검색 결과가 모두 선택되어 있는지 (전체 행 체크 표시 + 토글 방향 결정)
  const isAllFilteredSelected = useMemo(() => {
    if (isAllAsEmpty) return showAllChecked
    if (filteredValues.length === 0) return false
    const selected = new Set(value)
    return filteredValues.every((v) => selected.has(v))
  }, [isAllAsEmpty, showAllChecked, filteredValues, value])

  const handleToggleSelectAll = useCallback(() => {
    if (isAllAsEmpty) {
      // 전체 선택 ↔ 전부 해제 토글. 저장값은 어느 쪽이든 빈 배열(=전체)이다.
      setAllChecked(!showAllChecked)
      if (value.length > 0) onValueChange([])
      return
    }
    if (isAllFilteredSelected) {
      // 검색 결과가 모두 선택된 상태 → 검색 결과만 해제(필터 밖 선택은 유지)
      const removeSet = new Set(filteredValues)
      onValueChange(value.filter((v) => !removeSet.has(v)))
    } else {
      // 검색 결과만 추가 선택(기존 선택 유지)
      onValueChange([...new Set([...value, ...filteredValues])])
    }
  }, [isAllAsEmpty, showAllChecked, isAllFilteredSelected, filteredValues, value, onValueChange])

  // emptyMeansAll 모드의 개별 행 토글.
  // 전체 선택 상태에서 하나를 고르면 그 하나만 남고(전체 해제), 마지막 하나를 끄면 전부 해제 상태가 된다.
  const handleToggleOption = useCallback(
    (optValue: string) => {
      if (showAllChecked) {
        setAllChecked(false)
        onValueChange([optValue])
        return
      }
      onValueChange(value.includes(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue])
    },
    [showAllChecked, value, onValueChange],
  )

  const handleSearchChange = useCallback(
    (val: string) => {
      setSearchValue(val)
      onSearchValueChange?.(val)
    },
    [onSearchValueChange],
  )
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setSearchValue('')
        onSearchValueChange?.('')
      }
    },
    [onSearchValueChange],
  )
  const handleSearchClear = useCallback(() => {
    setSearchValue('')
    onSearchValueChange?.('')
  }, [onSearchValueChange])

  return (
    <Wrapper $width={width} $triggerHeight={triggerHeight} $placeholderAsValue={showAllChecked}>
      <MultiSelect.Root value={value} onValueChange={onValueChange} options={safeOptions}>
        <MultiSelect.Trigger
          // 전체 선택 상태는 저장값이 빈 배열이라 트리거가 placeholder 를 그린다 — 그 자리에 '전체' 를 보여 준다
          placeholder={showAllChecked ? '전체' : placeholder}
          floatingBlockLabel={floatingBlockLabel}
          required={required}
          disabled={disabled}
          summaryWithCount
        />
        <MultiSelect.OptionList width={width} maxHeight={300}>
          {showSearch && (
            <MultiSelect.OptionInput
              placeholder="검색"
              value={searchValue}
              onValueChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onClear={handleSearchClear}
            />
          )}
          {showSelectAll && filteredOptions.length > 0 && (
            <SelectAllRow
              role="button"
              tabIndex={0}
              aria-pressed={isAllFilteredSelected}
              onClick={handleToggleSelectAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleToggleSelectAll()
                }
              }}
            >
              <SelectAllCheck $checked={isAllFilteredSelected} aria-hidden>
                {isAllFilteredSelected ? '✓' : ''}
              </SelectAllCheck>
              전체
            </SelectAllRow>
          )}
          <MultiSelect.OptionGroup>
            {filteredOptions.map((opt) =>
              emptyMeansAll ? (
                <SelectAllRow
                  key={opt.value}
                  role="button"
                  tabIndex={0}
                  aria-pressed={showAllChecked || value.includes(opt.value)}
                  onClick={() => handleToggleOption(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleToggleOption(opt.value)
                    }
                  }}
                >
                  <SelectAllCheck $checked={showAllChecked || value.includes(opt.value)} aria-hidden>
                    {showAllChecked || value.includes(opt.value) ? '✓' : ''}
                  </SelectAllCheck>
                  {opt.label}
                  {showOptionValue && <OptionValue>{opt.value}</OptionValue>}
                </SelectAllRow>
              ) : (
                <MultiSelect.Option
                  key={opt.value}
                  value={opt.value}
                  label={opt.label}
                  suffixSubtle={showOptionValue ? opt.value : undefined}
                />
              ),
            )}
          </MultiSelect.OptionGroup>
        </MultiSelect.OptionList>
      </MultiSelect.Root>
    </Wrapper>
  )
}

export default SearchableMultiSelect
